import { map } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ModalsService } from '@core/services/modals/modals.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { DoodProductModel } from '@core/models/product.model';
import { CartItemWithOrigin } from '@core/models/cart.model';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { CloseModalOnBrowserBack } from '@core/services/modal-back-history/modal-close-on-back.decorator';
import { Store } from '@ngrx/store';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CartService } from '@core/services/cart/cart.service';
import { ProductKeys } from '@config/keys/product.keys';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { resolveCloseIconColors } from '@front-platform/content-builder';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { KioskProductCardFactoryComponent } from '@shared/atoms/kiosk/kiosk-product-cards/kiosk-product-card-factory/kiosk-product-card-factory.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import {
  bogofDesktopPageSize,
  resolveBogofDesktopProductsLayout,
} from './bogof-desktop-products-layout';

export type BogofModalModalConfig = ModalConfig<{
  handle: typeof BogofModalComponent.handle;
  inputs: {
    triggerProductId: string;
    promotionName: string;
    products: DoodProductModel[];
    loading?: boolean;
    modalMode?: 'products' | 'same-product' | 'same-but-different';
    triggerProduct?: DoodProductModel;
    cartItemToDuplicate?: CartItemWithOrigin;
    isComposition?: boolean;
    productShopId?: string;
    colors?: {
      close_icon_colors?: ContentBuilderFieldColors;
      productCardColors?: {
        textColor?: IContentBuilderFieldColor;
        cardColor?: IContentBuilderFieldColor;
        buttonBackgroundColor?: IContentBuilderFieldColor;
        unavailableButtonBackground?: IContentBuilderFieldColor;
        unavailableButtonText?: IContentBuilderFieldColor;
        buttonTextColor?: IContentBuilderFieldColor;
        priceColor?: IContentBuilderFieldColor;
      };
    };
  };
}>;

@CloseModalOnBrowserBack()
@Component({
  selector: 'app-bogof-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ContentBuilderColorPipe,
    TranslatePipe,
    ModalFillerAtomComponent,
    KioskProductCardFactoryComponent,
  ],
  templateUrl: './bogof-modal.component.html',
})
export class BogofModalComponent implements GetModalInterface<BogofModalModalConfig> {
  static handle = 'bogof-modal' as const;

  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly modalsService: ModalsService<BogofModalModalConfig> = inject(
    ModalsService<BogofModalModalConfig>,
  );
  private readonly orderStoreSelector = inject(OrderStoreSelector);
  private readonly translate = inject(TranslateService);
  private readonly cartService = inject(CartService);
  private readonly store = inject(Store);
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly mobileProductsScroller =
    viewChild<ElementRef<HTMLElement>>('mobileProductsScroller');

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);
  protected readonly isMobile = this.settingsStoreSelector.deviceIsMobileScreen;
  protected readonly deviceIsKiosk = this.settingsStoreSelector.isKioskLayout;

  protected readonly index = toSignal(
    this.modalsService.getData('bogof-modal').pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );
  private readonly liveModalData = toSignal(
    this.modalsService.getData('bogof-modal').pipe(map(el => el?.data)),
    { initialValue: undefined },
  );

  readonly triggerProductId = input.required<string>();
  readonly promotionName = input.required<string>();
  readonly products = input.required<DoodProductModel[]>();
  readonly loading = input(false);
  readonly modalMode = input<'products' | 'same-product' | 'same-but-different'>('products');
  readonly triggerProduct = input<DoodProductModel>();
  readonly cartItemToDuplicate = input<CartItemWithOrigin>();
  readonly isComposition = input(false);
  readonly productShopId = input<string>();
  readonly colors = input<{
    close_icon_colors?: ContentBuilderFieldColors;
    productCardColors?: {
      textColor?: IContentBuilderFieldColor;
      cardColor?: IContentBuilderFieldColor;
      buttonBackgroundColor?: IContentBuilderFieldColor;
      unavailableButtonBackground?: IContentBuilderFieldColor;
      unavailableButtonText?: IContentBuilderFieldColor;
      buttonTextColor?: IContentBuilderFieldColor;
      priceColor?: IContentBuilderFieldColor;
    };
  }>();

  readonly textColor = computed(() => this.resolvedColors()?.productCardColors?.textColor);
  readonly cardColor = computed(() => this.resolvedColors()?.productCardColors?.cardColor);
  readonly buttonBackgroundColor = computed(
    () => this.resolvedColors()?.productCardColors?.buttonBackgroundColor,
  );
  readonly unavailableButtonBackground = computed(
    () => this.resolvedColors()?.productCardColors?.unavailableButtonBackground,
  );
  readonly unavailableButtonText = computed(
    () => this.resolvedColors()?.productCardColors?.unavailableButtonText,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedColors()?.productCardColors?.buttonTextColor,
  );
  protected readonly defaultPriceColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };
  readonly priceColor = computed<IContentBuilderFieldColor>(
    () => this.resolvedColors()?.productCardColors?.priceColor ?? this.defaultPriceColor,
  );
  readonly resolvedTriggerProductId = computed(
    () => this.liveModalData()?.triggerProductId ?? this.triggerProductId(),
  );
  readonly resolvedPromotionName = computed(
    () => this.liveModalData()?.promotionName ?? this.promotionName(),
  );
  readonly resolvedProducts = computed(() => this.liveModalData()?.products ?? this.products());
  readonly resolvedLoading = computed(() => this.liveModalData()?.loading ?? this.loading());
  readonly resolvedModalMode = computed(
    () => this.liveModalData()?.modalMode ?? this.modalMode() ?? 'products',
  );
  readonly resolvedTriggerProduct = computed(
    () => this.liveModalData()?.triggerProduct ?? this.triggerProduct(),
  );
  readonly resolvedCartItemToDuplicate = computed(
    () => this.liveModalData()?.cartItemToDuplicate ?? this.cartItemToDuplicate(),
  );
  readonly resolvedIsComposition = computed(
    () => this.liveModalData()?.isComposition ?? this.isComposition(),
  );
  readonly resolvedProductShopId = computed(
    () => this.liveModalData()?.productShopId ?? this.productShopId(),
  );
  readonly resolvedColors = computed(() => this.liveModalData()?.colors ?? this.colors());
  readonly closeIconColors = computed(() => {
    const colors = this.resolvedColors();
    if (!colors) {
      return undefined;
    }
    const wrapped = { colors: colors as Record<string, unknown> };
    return resolveCloseIconColors(wrapped, wrapped);
  });
  readonly loadingCards = computed(() => [0, 1, 2]);
  readonly desktopLoadingCards = computed(() =>
    Array.from({ length: this.desktopPageSize() }, (_, index) => index),
  );
  readonly desktopCarouselIndex = signal(0);
  readonly showMobileScrollShadow = signal(false);
  protected readonly normalizedProducts = computed(() => {
    const value = this.resolvedProducts() as unknown;
    if (Array.isArray(value)) {
      return value.filter(isValidBogofProduct) as DoodProductModel[];
    }
    if (value && typeof value === 'object') {
      return Object.values(value).filter(isValidBogofProduct) as DoodProductModel[];
    }
    return [] as DoodProductModel[];
  });

  /** Products with price_to_display overridden to the "Offert" translation for BOGOF free items */
  protected readonly productsWithOfferPrice = computed(() => {
    const priceOffert = this.translate.instant('bogof.price-offert');
    return this.normalizedProducts().map(p => ({
      ...p,
      [ProductKeys.PriceToDisplay]: priceOffert,
      labels: [], // remove labels they should not be displayed in the modal
    })) as DoodProductModel[];
  });

  protected readonly desktopPageSize = computed(() =>
    bogofDesktopPageSize(this.kioskGlobalSettingsService.productCardChoice()),
  );
  protected readonly showDesktopCarousel = computed(
    () => this.productsWithOfferPrice().length > this.desktopPageSize(),
  );
  protected readonly maxDesktopCarouselIndex = computed(() =>
    Math.max(0, this.productsWithOfferPrice().length - this.desktopPageSize()),
  );
  protected readonly desktopDisplayedProducts = computed(() => {
    const products = this.productsWithOfferPrice();
    const index = this.desktopCarouselIndex();
    return products.slice(index, index + this.desktopPageSize());
  });
  protected readonly desktopProductsLayout = computed(() =>
    resolveBogofDesktopProductsLayout({
      displayedCount: this.desktopDisplayedProducts().length,
      productCardChoice: this.kioskGlobalSettingsService.productCardChoice(),
    }),
  );
  private readonly syncMobileScrollShadowEffect = effect(() => {
    this.resolvedLoading();
    this.productsWithOfferPrice().length;
    queueMicrotask(() => this.updateMobileScrollShadow());
  });
  private readonly syncDesktopCarouselIndexEffect = effect(() => {
    this.productsWithOfferPrice().length;
    if (this.desktopCarouselIndex() > this.maxDesktopCarouselIndex()) {
      this.desktopCarouselIndex.set(this.maxDesktopCarouselIndex());
    }
  });

  protected nextDesktopPage(): void {
    if (this.desktopCarouselIndex() < this.maxDesktopCarouselIndex()) {
      this.desktopCarouselIndex.update(v => v + 1);
    }
  }

  protected previousDesktopPage(): void {
    if (this.desktopCarouselIndex() > 0) {
      this.desktopCarouselIndex.update(v => v - 1);
    }
  }

  private updateMobileScrollShadow(): void {
    if (this.resolvedLoading()) {
      this.showMobileScrollShadow.set(false);
      return;
    }
    const el = this.mobileProductsScroller()?.nativeElement;
    if (!el) {
      this.showMobileScrollShadow.set(false);
      return;
    }
    const canScroll = el.scrollHeight > el.clientHeight + 1;
    if (!canScroll) {
      this.showMobileScrollShadow.set(false);
      return;
    }
    this.showMobileScrollShadow.set(true);
  }

  private selectProposal(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id ?? '',
        productOrigin: {
          channel: 'bogof',
          reference: `bogof_${this.resolvedTriggerProductId()}`,
          trigger_product_id: this.resolvedTriggerProductId(),
        },
      }),
    );
  }

  protected addProductDispatcher(product: DoodProductModel): void {
    this.selectProposal(product);
    this.close();
  }

  protected openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open<KioskProductOverviewModalConfig>(
      KioskProductOverviewModalComponent.handle,
      undefined,
      {
        image: product.images?.cover?.url,
        name: product.name,
        description: product.description,
        allergens: product.allergens,
        type: product.type,
        zIndex: KIOSK_PRODUCT_OVERVIEW_ABOVE_SALES_BOOSTER_INDEX,
        colors: {
          background: this.buttonTextColor(),
          accent: this.buttonBackgroundColor(),
          closeButton: this.buttonBackgroundColor(),
        },
      },
    );
  }

  protected yesSameProduct(): void {
    const item = this.resolvedCartItemToDuplicate();
    const product = this.resolvedTriggerProduct();
    if (!item || !product) return;

    this.cartService
      .addOrUpdateCartItem$({
        newCartItem: item,
        quantity: 1,
        product,
      })
      .subscribe({
        next: () => this.close(),
        error: () => {},
      });
  }

  protected sameProductChangeConfig(): void {
    const product = this.resolvedTriggerProduct();
    if (!product) return;

    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: this.triggerProductId(),
        productShopId: this.resolvedProductShopId() ?? product.shop_id ?? '',
        productOrigin: {
          channel: 'bogof',
          reference: `bogof_${this.resolvedTriggerProductId()}`,
          trigger_product_id: this.resolvedTriggerProductId(),
        },
      }),
    );
    this.close();
  }

  protected showOtherProductsFromSelection(): void {
    const triggerProduct = this.resolvedTriggerProduct();
    this.modalsService.setData('bogof-modal', {
      triggerProductId: this.resolvedTriggerProductId(),
      promotionName: this.resolvedPromotionName(),
      products: this.resolvedProducts(),
      loading: false,
      modalMode: 'products',
      triggerProduct,
      cartItemToDuplicate: this.resolvedCartItemToDuplicate(),
      isComposition: this.resolvedIsComposition(),
      productShopId: this.resolvedProductShopId() ?? triggerProduct?.shop_id ?? '',
      colors: this.resolvedColors(),
    });
  }

  close(): void {
    this.modalsService.close(BogofModalComponent.handle);
  }

  protected readonly unAvailableProductById = computed(() => {
    const errors = this.orderStoreSelector.selectErrorsSignal();
    return errors.violations
      ?.filter(
        violation =>
          violation.code &&
          [
            'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
            'PRODUCT_HIDDEN',
            'PRODUCT_NOT_AVAILABLE',
          ].includes(violation.code),
      )
      .map(violation => violation.data?.id as string)
      .reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
  });
}

function isValidBogofProduct(value: unknown): value is DoodProductModel {
  if (!value || typeof value !== 'object') return false;
  const maybeProduct = value as { id?: unknown; name?: unknown };
  return typeof maybeProduct.id === 'string' && maybeProduct.id.length > 0;
}
